import { PayFrequencyOptionValue } from 'handlers/yourTotalIncome';
import React from 'react';
import { useSelector } from 'react-redux';
import { getApplicationData } from 'selectors/getApplicationData';
import { getStudentLoanApplication } from 'selectors/getStudentLoanApplication';
import { getPreQualificationData } from 'selectors/preQualificationData';
import generatedPartners from 'generated/partners.json';

export type PartnerId = string;

/** Convenience constants for known partner ids (not exhaustive — new partners come from the API). */
export const KnownPartnerId = {
  Flasha: 'flasha',
  Fpma: 'fpma',
  Hillcrest: 'hillcrest',
  KingsDaughters: 'kings-daughters',
  SunyDownstate: 'suny-downstate',
  PhoebePutney: 'phoebe-putney',
  DelawareNursesAssociation: 'dna',
  UNCHealthLenoir: 'unc-health-lenoir',
  PinnacleHospital: 'pinnacle-hospital',
  IdahoGastro: 'idaho-gastro',
  HaskellMemorial: 'haskell-memorial',
  TerrebonneGeneral: 'terrebonne-general',
  MinidokaMemorial: 'minidoka-memorial',
  Nursegrid: 'nursegrid',
  HStream: 'hstream',
} as const;

export enum PartnerType {
  Employer = 'employer',
  Association = 'association',
  App = 'app',
  Authentication = 'authentication',
}

export type PublicPartnerRecord = {
  id: string;
  type: string;
  fullName?: string | null;
  shortName?: string | null;
  logoExtension?: string | null;
  /** ANY token match — same semantics as KnownEmployer.matchTerms */
  employerMatchTerms?: string[] | null;
  /** ALL tokens must match — same semantics as KnownEmployer.matchAllTerms */
  employerMatchAllTerms?: string[] | null;
  /** ANY normalized phrase substring — same semantics as KnownEmployer.matchPhrases */
  employerMatchPhrases?: string[] | null;
  paymentFrequency?: string | null;
  downloadPayrollInstructionsHtml?: string | null;
  directDepositInstructionsHtml?: string | null;
  benefitEligibleTitles?: string[] | null;
  debtConsolidationEnabled?: boolean | null;
  supportsStudentLoanApplicationPayrollDeduction?: boolean | null;
  supportsStudentLoanPaymentViaCardIn12Installments?: boolean | null;
};

export interface PartnerData {
  fullName?: string;
  shortName?: string;
  type?: PartnerType;
  logoExtension?: 'png' | 'svg';
  employerMatchTerms?: string[];
  employerMatchAllTerms?: string[];
  employerMatchPhrases?: string[];
  supportsStudentLoanApplicationPayrollDeduction?: boolean;
  supportsStudentLoanPaymentViaCardIn12Installments?: boolean;
  downloadPayrollInstructions?: JSX.Element;
  directDepositInstructions?: JSX.Element;
  benefitEligibleTitles?: string[];
  debtConsolidationEnabled?: boolean;
  paymentFrequency?: PayFrequencyOptionValue;
}

function htmlToElement(html?: string | null): JSX.Element | undefined {
  if (!html) return undefined;
  // eslint-disable-next-line react/no-danger
  return <div dangerouslySetInnerHTML={{ __html: html }} />;
}

function mapPublicPartner(record: PublicPartnerRecord): PartnerData {
  const type = Object.values(PartnerType).includes(record.type as PartnerType)
    ? (record.type as PartnerType)
    : undefined;

  return {
    fullName: record.fullName ?? undefined,
    shortName: record.shortName ?? undefined,
    type,
    logoExtension: (record.logoExtension as 'png' | 'svg' | undefined) || undefined,
    employerMatchTerms: record.employerMatchTerms ?? undefined,
    employerMatchAllTerms: record.employerMatchAllTerms ?? undefined,
    employerMatchPhrases: record.employerMatchPhrases ?? undefined,
    paymentFrequency: (record.paymentFrequency as PayFrequencyOptionValue) || undefined,
    downloadPayrollInstructions: htmlToElement(record.downloadPayrollInstructionsHtml),
    directDepositInstructions: htmlToElement(record.directDepositInstructionsHtml),
    benefitEligibleTitles: record.benefitEligibleTitles ?? undefined,
    debtConsolidationEnabled: record.debtConsolidationEnabled ?? undefined,
    supportsStudentLoanApplicationPayrollDeduction: record.supportsStudentLoanApplicationPayrollDeduction ?? undefined,
    supportsStudentLoanPaymentViaCardIn12Installments:
      record.supportsStudentLoanPaymentViaCardIn12Installments ?? undefined,
  };
}

const partnerRecords = generatedPartners as PublicPartnerRecord[];

export const PARTNER_DATA: Record<string, PartnerData> = Object.freeze(
  Object.fromEntries(partnerRecords.map((record) => [record.id, mapPublicPartner(record)])),
);

export const getPartnerName = (partnerId: PartnerId) => {
  const fromData = PARTNER_DATA[partnerId]?.shortName || PARTNER_DATA[partnerId]?.fullName;
  if (fromData) return fromData;

  return partnerId
    .split('-')
    .map((word) => `${word.charAt(0).toUpperCase()}${word.substring(1)}`)
    .join(' ');
};

/**
 * Mirrors EmployersDataService.getKnownEmployer matching:
 * - employerMatchTerms: any token present
 * - employerMatchAllTerms: all tokens present
 * - employerMatchPhrases: any normalized phrase substring
 */
const normalizeEmployerPhrase = (value: string): string =>
  value
    .toLocaleLowerCase()
    .replace(/['']/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const getPartnerFromEmployerName = (employerName?: string): PartnerId | undefined => {
  if (!employerName) return undefined;

  const employerNameArr = employerName
    .replace(/'/g, '')
    .split(' ')
    .map((word) => word.toLocaleLowerCase());
  const normalizedEmployerName = normalizeEmployerPhrase(employerName);

  return Object.keys(PARTNER_DATA).find((partnerId) => {
    const partner = PARTNER_DATA[partnerId];
    return (
      partner.employerMatchTerms?.some((term) => employerNameArr.includes(term.toLowerCase())) ||
      (partner.employerMatchAllTerms?.length
        ? partner.employerMatchAllTerms.every((term) => employerNameArr.includes(term.toLowerCase()))
        : false) ||
      partner.employerMatchPhrases?.some((phrase) => normalizedEmployerName.includes(normalizeEmployerPhrase(phrase)))
    );
  });
};

export const usePartnerData = (): PartnerData & { partnerName?: PartnerId } => {
  const { application } = useSelector(getApplicationData);
  let { partnerId: partnerName } = useSelector(getPreQualificationData);

  const {
    applicationData: { employerName: studentLoanEmployerName },
  } = useSelector(getStudentLoanApplication);

  const employerName = application?.employment.employer1.employerName ?? studentLoanEmployerName;
  partnerName = getPartnerFromEmployerName(employerName) ?? partnerName;

  const partnerData = (partnerName ? PARTNER_DATA[partnerName] : null) ?? {};

  return { ...partnerData, partnerName };
};
